import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Award, CheckCircle, Clock, Globe, Shield, FileText, Briefcase } from 'lucide-react';
import { MOCK_CONSULTANCIES } from '../constants';

export const ConsultancyProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const consultancy = MOCK_CONSULTANCIES.find(c => c.id === id);

  if (!consultancy) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Consultancy Not Found</h2>
          <p className="text-slate-500 mb-6">The consultancy you are looking for does not exist or has been removed.</p>
          <Link to="/directory" className="text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header / Breadcrumb */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/directory" className="text-slate-500 hover:text-slate-800 text-sm font-medium flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Directory
          </Link>
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <img 
              src={consultancy.logoUrl} 
              alt={consultancy.name} 
              className="w-20 h-20 rounded-lg object-cover border border-slate-100 shadow-sm"
            />
            <div className="flex-grow">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{consultancy.name}</h1>
                {consultancy.isSponsored && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded uppercase tracking-wide">
                    Verified Partner
                  </span>
                )}
              </div>
              <p className="text-lg text-blue-600 font-medium">{consultancy.tagline}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-900 hover:bg-blue-800 transition-colors shadow-sm">
                Request Proposal
              </button>
              <button className="inline-flex items-center justify-center px-6 py-3 border border-slate-300 text-base font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors">
                Contact Team
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">About the Firm</h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                {consultancy.description}
              </p>
              
              <div className="mt-6 flex flex-wrap gap-3">
                {consultancy.badges.map(badge => (
                  <span key={badge} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-100">
                    <Award className="w-4 h-4 mr-2" /> {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Services & Expertise */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Core Competencies</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Services
                  </h3>
                  <ul className="space-y-2">
                    {consultancy.services.map(service => (
                      <li key={service} className="text-slate-700 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></span>
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Therapeutic Areas
                  </h3>
                  <ul className="space-y-2">
                    {consultancy.therapeuticAreas.map(area => (
                      <li key={area} className="text-slate-700 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></span>
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Track Record Mockup */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900">Verified Track Record</h2>
                <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm font-medium">
                   <Shield className="w-4 h-4 mr-1.5" /> Data Verified
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                 <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-3xl font-bold text-slate-900">{consultancy.verifiedApprovals}</div>
                    <div className="text-sm text-slate-500 mt-1">Major Approvals</div>
                 </div>
                 <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-3xl font-bold text-slate-900">100%</div>
                    <div className="text-sm text-slate-500 mt-1">Response Rate</div>
                 </div>
                 <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-3xl font-bold text-slate-900">{new Date().getFullYear() - consultancy.foundedYear}+</div>
                    <div className="text-sm text-slate-500 mt-1">Years Experience</div>
                 </div>
              </div>
            </div>
          </div>

          {/* Sidebar Stats */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">At a Glance</h3>
              </div>
              <div className="p-6 space-y-6">
                <div>
                   <div className="flex items-center text-slate-500 text-sm mb-1">
                     <Clock className="w-4 h-4 mr-2" /> Rate Card
                   </div>
                   <div className="text-lg font-semibold text-slate-900">
                     ${consultancy.hourlyRateStart} - ${consultancy.hourlyRateEnd} <span className="text-sm font-normal text-slate-500">/ hour</span>
                   </div>
                </div>

                <div>
                   <div className="flex items-center text-slate-500 text-sm mb-1">
                     <Users className="w-4 h-4 mr-2" /> Team Size
                   </div>
                   <div className="text-lg font-semibold text-slate-900">
                     {consultancy.teamSize} Employees
                   </div>
                </div>

                <div>
                   <div className="flex items-center text-slate-500 text-sm mb-1">
                     <Clock className="w-4 h-4 mr-2" /> Established
                   </div>
                   <div className="text-lg font-semibold text-slate-900">
                     {consultancy.foundedYear}
                   </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Global Reach</h3>
              </div>
              <div className="p-6">
                 <div className="space-y-3">
                    {consultancy.regions.map(region => (
                      <div key={region} className="flex items-center text-slate-700">
                        <Globe className="w-4 h-4 mr-3 text-slate-400" />
                        {region}
                      </div>
                    ))}
                 </div>
                 <div className="mt-6 pt-6 border-t border-slate-100">
                    <p className="text-xs text-slate-500 leading-relaxed">
                      This consultancy has verified experience interacting with the Health Authorities listed above.
                    </p>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};